import { DashboardViews } from 'src/components/Layouts/DashboardLayout/types'
import {
  CheckoutType,
  ICheckout,
  InputMaybe,
  IPhoto,
  IUser,
  Maybe
} from '../types/graphql'
import {
  CheckoutView,
  IFilterCanStoreVehicle,
  IPrivatePhotoInput,
  RedirectUrls
} from '../types/types'

const DecimalTwoDigitsRegularExpression =
  /^(?:0\.(?:0[0-9]|[0-9]\d?)|[0-9]\d*(?:\.\d{1,2})?)(?:e[+-]?\d+)?$/

const TwoDecimalRegularExpression = /^\d+(\.\d{1,2})?$/

function convertRangeToZoom(range: number): number {
  let zoom = Math.round(Math.log(35200000 / range) / Math.log(2))
  if (zoom < 0) zoom = 0
  else if (zoom > 19) zoom = 19
  return zoom
}

function convertZoomToRange(zoom: number): number {
  let range = 35200000 / Math.pow(2, zoom)
  if (range < 300) range = 300
  return range
}

const convertZoomToRangeString = (value: number): string => {
  const range = convertZoomToRange(value)
  if (range > 1000) {
    return (range / 1000).toFixed(0) + 'km'
  }
  return range.toFixed(0) + 'm'
}

function hasBeenTwoDays(
  date: string,
  isAdmin: Maybe<boolean> | undefined
): boolean {
  const date1 = new Date(date)
  const date2 = new Date()
  const hours = Math.abs(date1.getTime() - date2.getTime()) / 36e5
  // Admins can rescue forgotten approvals for up to 6 weeks (the Expired tab
  // surfaces them); hosts keep the 2-day window. Mirror of the server
  // hasBeenTwoDays in spotme-server CheckoutService.ts — change both together.
  const days = isAdmin ? 42 : 2
  return hours > days * 24
}

function hoursRemainingCheckout({ completedAt }: ICheckout): string {
  const now = new Date()
  const target = new Date(completedAt as string)
  const expiration = new Date(target.setDate(target.getDate() + 2))
  const hours = Math.abs(now.getTime() - expiration.getTime()) / 36e5
  return hours.toFixed(0)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStepIndex = (stepName: string, flow: any[]) => {
  return flow.findIndex((item) => item.id === stepName)
}

function validEmail(email: string): boolean {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email.toLowerCase())
}

function validPassword(password: string): boolean {
  const re = new RegExp(
    '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})'
  )
  return re.test(password)
}

function convertPhotoInput(photo: IPhoto): IPrivatePhotoInput {
  return {
    id: photo.id ?? '',
    url: photo.url ?? '',
    name: photo.name ?? '',
    width: photo.width,
    height: photo.height
  }
}

export function formatDateSimple(d: string | undefined | null): string {
  const date = new Date(d ?? new Date())
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()
  return year + '/' + month + '/' + day
}

function formatDatestamp(date: string | undefined | null): string {
  const lang = 'es-US'
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }

  return !date
    ? new Date().toLocaleString(lang, options)
    : new Date(date).toLocaleString(lang, options)
}

function formatTimestamp(date: string | undefined | null): string {
  const lang = 'es-US'
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZone: 'America/Mexico_City'
  }

  return !date
    ? new Date().toLocaleString(lang, options)
    : new Date(date).toLocaleString(lang, options)
}

function formatTimestampTime(date: string | undefined | null): string {
  const lang = 'es-US'
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: 'America/Mexico_City'
  }

  return !date
    ? new Date().toLocaleString(lang, options)
    : new Date(date).toLocaleString(lang, options)
}

// Deterministic — see the "HYDRATION-SAFE FORMATTERS" block below.
// Handles the two option patterns current callers actually use:
//   1. default                                     → "14 may"
//   2. { month: 'long', year: 'numeric',
//        day: undefined }                          → "mayo de 2026"
function formatTimestampMonthDay(
  date: string | undefined | null,
  opt?: Intl.DateTimeFormatOptions
): string {
  // A missing date renders NOTHING — it must never fall back to `new Date()`.
  // That fallback made this "hydration-safe" helper non-deterministic: SSG/ISR
  // HTML baked the build-time month while the browser rendered the current
  // one, so every cached page drifted the moment the month rolled over →
  // React #418 (`Anfitrión desde julio` SSR vs `agosto` CSR, hydration_diff
  // 2026-08-01/08-05). It also printed a false date for absent values.
  // Callers that genuinely want "now" must pass an explicit timestamp.
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const mx = new Date(d.getTime() + MEXICO_CITY_OFFSET_MS)
  const day = mx.getUTCDate()
  const monthIdx = mx.getUTCMonth()
  const year = mx.getUTCFullYear()

  const monthLong = opt?.month === 'long'
  const showYear = opt?.year === 'numeric'
  // `day: undefined` (explicitly passed) hides the day. Default is to show it.
  const showDay = opt && 'day' in opt ? opt.day !== undefined : true

  const monthName = monthLong
    ? SPANISH_MONTHS_LONG[monthIdx]
    : SPANISH_MONTHS_SHORT[monthIdx]

  const dayPart = showDay
    ? monthLong
      ? `${day} de ${monthName}`
      : `${day} ${monthName}`
    : monthName

  const yearPart = showYear ? (monthLong ? ` de ${year}` : ` ${year}`) : ''

  return dayPart + yearPart
}

// =============================================================================
// HYDRATION-SAFE FORMATTERS — DO NOT REPLACE WITH `toLocaleString` / `Intl.*`
// =============================================================================
//
// Background: `(1500).toLocaleString('es-MX')` returns `"1,500"` on Node 22
// but `"1 500"` (narrow no-break space, U+202F) on modern Chrome. Same for
// `'es-US'` and `'es'`. `toLocaleDateString('es-MX', { month: 'short' })`
// has a similar Node-vs-Chrome divergence (`"may"` vs `"may."`). When this
// runs in render on a page reachable from `getStaticProps` /
// `getServerSideProps`, server HTML and client first render diverge →
// React hydration error #418 → full client re-render.
//
// We've shipped this regression three times. The deterministic helpers
// below are the fix — they do NOT consult ICU. **Do not "simplify" them
// back to `toLocaleString` / `Intl.NumberFormat` / `Intl.DateTimeFormat`.**
//
// New formatters: add a new deterministic helper here. Don't reach for
// `Intl.*` in component code. See:
//   - spotme-client/CLAUDE.md → "Hydration-Safe Number & Date Formatting"
// =============================================================================

// Mexico City has no DST since 2022 → fixed UTC-6 offset.
const SPANISH_MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic'
] as const
const SPANISH_MONTHS_LONG = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre'
] as const
const MEXICO_CITY_OFFSET_MS = -6 * 60 * 60 * 1000

function formatTimestampMonthDayYear(date: string | undefined | null): string {
  // No `new Date()` fallback — see the note in `formatTimestampMonthDay`.
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const mx = new Date(d.getTime() + MEXICO_CITY_OFFSET_MS)
  const day = mx.getUTCDate()
  const month = SPANISH_MONTHS_SHORT[mx.getUTCMonth()]
  const year = mx.getUTCFullYear()
  return `${day} ${month} ${year}`
}

/**
 * A DATE-ONLY value carried as a UTC-midnight timestamp (e.g. Stripe's payout
 * `arrival_date`), read in UTC. `formatTimestampMonthDayYear` shifts to Mexico
 * City (UTC-6) and would print the previous day for these.
 */
export function formatUtcDateMonthDayYear(
  date: string | undefined | null
): string {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const month = SPANISH_MONTHS_SHORT[d.getUTCMonth()]
  return `${d.getUTCDate()} ${month} ${d.getUTCFullYear()}`
}

/**
 * `YYYY-MM-DD` for a `<input type="date">` / date picker, read as the **Mexico
 * City** calendar date of the timestamp — matches `formatTimestampMonthDayYear`
 * (which is MX-fixed), so a picker/reload shows the SAME day the rest of the app
 * displays. Use this to seed a date input from a stored ISO (or today).
 *
 * Fixes the off-by-one where `new Date(iso).toISOString().split('T')[0]` reads
 * the UTC date: a value stored at (or near) UTC midnight reads back as the
 * previous MX day.
 */
export function toDateInputValue(date: string | number | Date): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''
  const mx = new Date(d.getTime() + MEXICO_CITY_OFFSET_MS)
  const y = mx.getUTCFullYear()
  const m = String(mx.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(mx.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

/**
 * ISO timestamp for a `YYYY-MM-DD` date-input value, anchored at **noon UTC**
 * (`T12:00:00Z` = 6 AM CDMX billing anchor) — the SAME normalization
 * `BaseDatePicker` applies (`setUTCHours(12,0,0,0)`). Stored this way, the date
 * renders as the picked calendar day everywhere (`formatTimestampMonthDayYear`
 * is MX-fixed) and lines up with the checkout/subscription anchor convention.
 *
 * Use this for any date-only field whose picker value is a plain `YYYY-MM-DD`
 * string (not the Controller-mode GMT string BaseDatePicker emits directly).
 *
 * Fixes the "shows yesterday" bug: `new Date('2026-07-01')` parses as UTC
 * midnight → in MX (UTC-6) that's Jun 30 18:00 → displays "30 jun".
 */
export function dateInputToIso(yyyyMmDd: string): string {
  return new Date(`${yyyyMmDd}T12:00:00.000Z`).toISOString()
}

function getYearDate(date: string | undefined | null): string {
  if (!date) {
    return new Date().toLocaleDateString('en-us', {
      year: 'numeric'
    })
  }

  return new Date(date).toLocaleDateString('en-us', {
    year: 'numeric'
  })
}

// See the "HYDRATION-SAFE FORMATTERS" block above before editing.
function formatNumberWithCommas(num: number, decimals = 0): string {
  const fixed = num.toFixed(decimals)
  const [whole, frac] = fixed.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return frac ? `${grouped}.${frac}` : grouped
}

function numberWithCommas(
  num: number | string | undefined | null
): string | undefined {
  if (num === undefined || num === null) return undefined
  if (isNaN(num as number)) return ''
  const parsed = typeof num === 'string' ? parseFloat(num) : num
  if (parsed === undefined || parsed === null || isNaN(parsed)) return ''
  // Match prior behavior: trim trailing zeros via toFixed(2) → Number(...)
  const decimal = Number(parsed.toFixed(2))
  const decimals = Number.isInteger(decimal) ? 0 : 2
  return formatNumberWithCommas(decimal, decimals)
}

function numberWithCommasAndDecimal(
  x: Maybe<number> | undefined
): string | undefined {
  if (x === undefined || x === null) return undefined
  return formatNumberWithCommas(x, 2)
}

// Deterministic — see the "HYDRATION-SAFE FORMATTERS" block above.
// Was `.toLocaleString('es-US')` which diverges between Node (`"2,100"`) and
// modern Chrome (`"2 100"` with U+202F). Source of #418 across listing
// cards, price cards, size labels, and the listing detail page.
function numberRounedUpWithoutDecimals(
  x: Maybe<number> | undefined
): string | undefined {
  return formatNumberWithCommas(Math.round(x ?? 0), 0)
}

export const decimalsWhenNeeded = (num: number) =>
  num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)

/**
 * Format a space's width × length as a clean LINEAR dimension string.
 * e.g. `{ width: 2.5, length: 5 }` → `"2.5 × 5 m"`. Deterministic (no Intl) —
 * safe on SSR/SSG surfaces. Returns `''` when either dimension is missing.
 *
 * NOTE: this is LINEAR (two lengths), NOT area — do NOT append "²". The old
 * inline `{width}m x {length}m²` rendered "2.5m x 5m²", which wrongly implied
 * the 5 was squared. A 2.5 × 5 space is 12.5 m², not "5 m²".
 */
function formatDimensions(
  size: { width?: Maybe<number>; length?: Maybe<number> } | null | undefined
): string {
  const w = size?.width
  const l = size?.length
  if (w === undefined || w === null || l === undefined || l === null) return ''
  return `${decimalsWhenNeeded(w)} × ${decimalsWhenNeeded(l)} m`
}

function addressSplit(address: Maybe<string> | undefined): {
  country: string
  state: string
  city?: string
} {
  const split = address?.split(',')

  if (!split || split.length <= 0)
    return {
      country: '',
      state: ''
    }

  const length = split.length
  try {
    const country = split[length - 1].trim()
    const state = split[length - 2].replace(/[0-9]/g, '').trim()

    if (length <= 3) {
      return { state, country }
    } else {
      const city = split[length - 3].replace(/[0-9]/g, '').trim()
      return { state, country, city }
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  } catch (_) {
    return {
      country: '',
      state: ''
    }
  }
}

function displayAddress(address: Maybe<string> | undefined): string {
  if (!address) return ''
  const split = address?.split(',')

  if (!split || split.length <= 0) return ''

  const length = split.length
  try {
    const country = split[length - 1]
    const state = split[length - 2].replace(/[0-9]/g, '').trim()

    if (length < 3) {
      return `${state}, ${country}`
    } else {
      const city = split[length - 3].replace(/[0-9]/g, '').trim()
      return `${city}, ${state}`
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  } catch (_) {
    return ''
  }
}

const externalImageLoader = ({ src }: { src: string }) => src

const getSubmitLocationUrl = (isAuthenticated: boolean) =>
  isAuthenticated
    ? RedirectUrls.Listings
    : `${RedirectUrls.Login}?redirectL=${encodeURIComponent(
        RedirectUrls.Listings
      )}`

const getCheckoutTabAndView = (
  currentUser: Maybe<IUser>,
  checkout: ICheckout
): {
  checkoutTab: CheckoutType
  checkoutView: CheckoutView
  dashboardPage: RedirectUrls
  dashboardView: DashboardViews
} => {
  const dashboardView =
    currentUser?.id === checkout.renterId
      ? DashboardViews.Rent
      : DashboardViews.Host
  const checkoutView =
    currentUser?.id === checkout.renterId
      ? CheckoutView.Rent
      : CheckoutView.Reservation
  // Mirrors the ES tab filters in spotme-server/src/datasources/ElasticSearch/
  // checkout.ts (getFilterSortByType) so the back-link lands on the tab that
  // actually lists the checkout. Rejected and lapsed-unanswered requests live
  // on the Expired tab (unless cancelled, which Past already covers). The
  // Pending window is the ES one (2 days, NOT the admin 14-day assess window).
  const checkoutTab = checkout.hasEnded
    ? CheckoutType.Past
    : checkout.landlordAssessedAt && !checkout.isLandlordApproved
      ? checkout.cancelledAt
        ? CheckoutType.Past
        : CheckoutType.Expired
      : checkout.startedAt && checkout.isLandlordApproved
        ? CheckoutType.Active
        : checkout.landlordAssessedAt && checkout.isLandlordApproved
          ? CheckoutType.UpComing
          : checkout.cancelledAt
            ? CheckoutType.Past
            : checkout.completedAt &&
                !hasBeenTwoDays(checkout.completedAt, false)
              ? CheckoutType.Pending
              : CheckoutType.Expired
  const dashboardPage =
    checkoutView === CheckoutView.Rent
      ? RedirectUrls.Reservations
      : RedirectUrls.Rents

  return {
    checkoutTab,
    checkoutView,
    dashboardPage,
    dashboardView
  }
}

async function fetchWithTimeout(
  resource: RequestInfo,
  options?: RequestInit & { timeout?: number }
): Promise<Response> {
  const { timeout = 8000 } = options ?? {}
  const controller = new AbortController()
  const id = setTimeout(() => controller.abort(), timeout)
  const response = await fetch(resource, {
    ...options,
    signal: controller.signal
  })
  clearTimeout(id)
  return response
}

function getUserName(user: IUser): string {
  return `${
    user.firstname ?? user.userChanges?.firstname ?? 'Usuario Anonimo'
  } ${user.lastname ?? user.userChanges?.lastname ?? ''}`
}

function mapCanStoreVehicleToRadio(
  value: InputMaybe<boolean> | undefined
): IFilterCanStoreVehicle {
  if (value === true) return IFilterCanStoreVehicle.Vehicle
  if (value === false) return IFilterCanStoreVehicle.SelfStorage
  return IFilterCanStoreVehicle.Both
}

function mapRadioToCanStoreVehicle(
  value: IFilterCanStoreVehicle
): boolean | undefined {
  switch (value) {
    case IFilterCanStoreVehicle.Vehicle:
      return true
    case IFilterCanStoreVehicle.SelfStorage:
      return false
    case IFilterCanStoreVehicle.Both:
    default:
      return undefined
  }
}

function isANumber(value: string) {
  return !isNaN(parseFloat(value))
}

function isNonNegative(num: number): boolean {
  return num >= 0
}

function dateIsValid(date: Date) {
  return !Number.isNaN(new Date(date).getTime())
}

/**
 * Stripe Connect's floor for a connected account's DOB. Below it,
 * `accounts.create` 400s with "This value must be greater than or equal to
 * 1,900" — and because the account is only created when an ADMIN approves the
 * KYC submission, a typo'd year blocks verification days later with no signal
 * to the user. Mirrored server-side in `spotme-server`'s `validateFields`.
 */
export const MIN_BIRTH_YEAR = 1900

/**
 * Sanity bounds for a self-reported birthdate: parseable, year >= 1900, not in
 * the future. This is exactly what the MUI picker's `minDate`/`maxDate` claim
 * to enforce but don't — those props only style the calendar and flag the
 * field; a typed out-of-range date still reaches the change handler as a
 * perfectly valid `Date` and gets written to the form. Wire this into the
 * react-hook-form `validate` rule so the bound is actually enforced.
 *
 * NOT an age check — the 18+ rule lives in the KYC AI gate + admin review.
 */
function isBirthdateInRange(value: unknown): boolean {
  if (value == null || value === '') return false
  const date = new Date(value as string | number | Date)
  if (Number.isNaN(date.getTime())) return false
  return date.getUTCFullYear() >= MIN_BIRTH_YEAR && date.getTime() <= Date.now()
}

function getStepProgressBarPecentage(stepArray: string[], step: string) {
  return Number(
    ((stepArray.indexOf(step) / (stepArray.length - 1)) * 100).toFixed(2)
  )
}

export const phoneRegex = new RegExp(
  '([a-zA-Z0-9_\\-\\.]+)@([a-zA-Z0-9_\\-\\.]+)\\.([a-zA-Z]{2,5})'
)

// Contact-info validation for listing text lives in src/utils/contactInfo.ts
// (logic-identical mirror of the server's detectContactInfoInText — the old
// weaker regex here let URLs/social handles through client validation only to
// be auto-rejected server-side).

export const [MIN_PRICE, MAX_PRICE] = [0, 999999]
export function valueLabelFormat(value: number) {
  // Convert the slider value back to actual price
  const actualValue = value === 0 ? 0 : Math.pow(10, value / 30) - 1

  if (actualValue >= 1000000) {
    return `$${(actualValue / 1000000).toFixed(1)}M`
  } else if (actualValue >= 1000) {
    return `$${(actualValue / 1000).toFixed(0)}K`
  }

  return `$${Math.round(actualValue)}`
}

export {
  addressSplit,
  convertPhotoInput,
  convertRangeToZoom,
  convertZoomToRange,
  convertZoomToRangeString,
  dateIsValid,
  DecimalTwoDigitsRegularExpression,
  displayAddress,
  externalImageLoader,
  fetchWithTimeout,
  formatDatestamp,
  formatDimensions,
  formatTimestamp,
  formatTimestampMonthDay,
  formatTimestampMonthDayYear,
  formatTimestampTime,
  getCheckoutTabAndView,
  getStepProgressBarPecentage,
  getSubmitLocationUrl,
  getUserName,
  getYearDate,
  hasBeenTwoDays,
  hoursRemainingCheckout,
  isANumber,
  isBirthdateInRange,
  isNonNegative,
  mapCanStoreVehicleToRadio,
  mapRadioToCanStoreVehicle,
  numberRounedUpWithoutDecimals,
  numberWithCommas,
  numberWithCommasAndDecimal,
  TwoDecimalRegularExpression,
  validEmail,
  validPassword
}
